import { type JSX, useEffect, useState } from 'react';
import type { ComponentType } from 'react';

import dynamic from 'next/dynamic';

/**
 * Load a component by:
 *   1. Waiting for a condition to be true
 *   2. Kicking off it's dynamic promise at that time
 *
 * The default dynamic import from next/dynamic only sort-of saves bundle size
 * but it still contributes a lot to active parsing time.
 */
export function DynamicComponentWithCondition<T extends object>(
  props: T & {
    dynamicLoaderFunction: () => Promise<ComponentType<T>>;
    loadingCondition: boolean;
    children?: JSX.Element;
  },
): JSX.Element {
  const [LoadableComponent, setLoadableComponent] = useState<ComponentType<T> | null>(null);
  useEffect(() => {
    if (!props.loadingCondition) {
      return;
    }
    // oxlint-disable-next-line react/set-state-in-effect -- react19-baseline: pre-existing, do not copy. Derive during render or set from the event instead
    setLoadableComponent(dynamic<T>(props.dynamicLoaderFunction, { ssr: false }));
    // oxlint-disable-next-line react/exhaustive-effect-dependencies, react/exhaustive-deps -- react19-baseline: pre-existing, do not copy. Fix the dependency array rather than suppressing. Add the missing dependencies rather than suppressing
  }, [props.loadingCondition]);
  const componentProps: T = {
    ...props,
    dynamicLoaderFunction: undefined,
    loadingCondition: undefined,
  };

  return props.loadingCondition && LoadableComponent ? (
    <LoadableComponent {...componentProps} />
  ) : (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <></>
  );
}
